export function validateNamespaceOneDepthLevel(term) {
  if (typeof term === 'string' && term.split('.').length >= 2) {
    return;
  }

  throw new Error('Nome de evento inválido.\nO nome do evento deve ser uma string com namespace'
  + 'com pelo 1 nível de profundidade, exemplo: "alarms.autorefresh", "mySigma.nonConfiguredArea".');
}

const emptyEventNameValidator = () => null;

/**
 * Event Bus / Barramento de eventos
 *
 * Event bus simples para ouvintes e publicadores.
 *
 * Os nome de eventos usados aqui devem ser no formato "namespace.evento".
 * O namespace deve ter pelo menos 1 nível de profundidade a fim de evitar colisão de eventos,
 * exemplo: "myData.save", "myData.load", "profile.save".
 *
 * Os ouvintes devem ser funções formato "function(...args: any[]): void",
 * podendo ter 0 ou muitos argumentos passados no evento.
 */
export class EventBus {
  /**
   * @private
   * Coleção de ouvintes de todos os eventos pendurados.
   */
  listenersCollection = null;

  /**
   * Validador de nomes de eventos;
   * @readonly
   */
  eventNameValidator

  /**
   * Construtor base.
   * @param {Function} [eventNameValidator] Função validadora de nomes de eventos.
   */
  constructor({ eventNameValidator } = {}) {
    this.listenersCollection = {};
    this.eventNameValidator = eventNameValidator || emptyEventNameValidator;
  }

  /**
   * Adiciona um ouvinte a um evento.
   * @param {String} eventName Nome do evento.
   * @param {Function} fn Função ouvinte do evento.
   * @param {Any} [scope] Escopo de execução (this) para a função ouvinte.
   */
  on(eventName, fn, scope = null) {
    this.eventNameValidator(eventName);
    this.getListeners(eventName).push({
      fn,
      scope,
    });
  }

  /**
   * Adiciona ouvintes a eventos em um objeto de definições.
   * @param {Object} events Object de definições no formato { eventName: fn, ... }.
   * @param {Any} [scope] Escopo de execução (this) para a função ouvinte.
   */
  onEach(events, scope = null) {
    Object.keys(events).forEach(eventName => this.on(eventName, events[eventName], scope));
  }

  /**
   * Remove um ouvinte de um evento. As referências passadas aqui devem ser as mesmas
   * usadas para adicionar o ouvinte.
   * @param {String} eventName Nome do evento.
   * @param {Function} fn Função ouvinte do evento.
   * @param {Any} [scope] Escopo de execução (this) para a função ouvinte.
   */
  off(eventName, fn, scope = null) {
    const listeners = this.getListeners(eventName);
    const index = listeners.findIndex(listener => listener.fn === fn
      && listener.scope === scope);

    if (index === -1) {
      throw new Error(`Não é possível remover o ouvinte. Ouvinte não encontrado para o evento "${
        eventName
      }". Verifique a referência da função e do escopo.`);
    }

    listeners.splice(index, 1);
    if (!listeners.length) {
      this.clearListeners(eventName);
    }
  }

  /**
   * Emitir um evento através do barramento para todos os ouvintes.
   * @param {String} eventName Nome do evento.
   * @param  {...any} [args] Argumentos do evento. Podem ser omitidos para um evento sem
   * parametros.
   */
  emit(eventName, ...args) {
    if (this.hasListenersConfig(eventName)) {
      this.getListeners(eventName)
        .forEach(listener => listener.fn.call(listener.scope, ...args));
    }
  }

  /**
   * Método privado para uso interno.
   * Retorna os ouvintes de um evento de maneira segura, evitando retornos nulos.
   * @private
   * @param {String} eventName Nome do evento.
   * @returns {Object[]} Array de listeners do Evento.
   */
  getListeners(eventName) {
    if (!this.hasListenersConfig(eventName)) {
      this.listenersCollection[eventName] = [];
    }
    return this.listenersCollection[eventName];
  }

  /**
   * Método privado para uso interno.
   * Informa se há configuração de listeners para um evento.
   * @private
   * @param {String} eventName Nome do evento.
   */
  hasListenersConfig(eventName) {
    return !!this.listenersCollection[eventName];
  }

  /**
   * Método privado para uso interno.
   * Limpa todos os ouvintes de um evento.
   * @private
   * @param {String} eventName Nome do evento.
   */
  clearListeners(eventName) {
    delete this.listenersCollection[eventName];
  }

  /**
   * Cria uma interface de procurador para um eventBus, expondo apenas os métodos relacionados
   * a observação, como On, Off, Emit, e etc.
   * @returns {Object}
   */
  createObservableProxy() {
    return {
      on: (...args) => this.on(...args),
      onEach: (...args) => this.onEach(...args),
      off: (...args) => this.off(...args),
      emit: (...args) => this.emit(...args),
    };
  }
}

/**
 * Barramento global de eventos.
 */
export default new EventBus({ eventNameValidator: validateNamespaceOneDepthLevel });
